import { type ReactNode, useId, useLayoutEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useLoaderData, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { filterNonNil, whileActive } from '@/utils/rx-util'
import { find, map, observeOn, switchMap, tap } from 'rxjs/operators'
import { useObservable, useSubject } from '@/hooks/rx-hooks'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { links, parseSearchParamsToEditDraftJobOptions } from '@/utils/link'
import { useRootStore } from '@/contexts/root-store-context'
import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { useActivation } from '@/hooks/view-model-hooks'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import {
  useWizardBottomBarPrimaryButton,
  Wizard,
  WizardBottomBar,
  WizardBottomBarLabel,
  WizardBottomBarPrimaryButton,
  WizardBottomBarPrimaryButtonContextProvider,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentFooter,
  WizardPageAnimatedContent,
  WizardPageLayout,
  WizardPageMainColumn,
  WizardPageStickyColumn,
  WizardTopBar,
  WizardTopBarLogo,
} from '@/components/Wizard'
import {
  useDisableGlobalNavigationIndicator,
  useHideGlobalNavbar,
} from '@/features/shell/global-navigation/GlobalNavigationContainer'
import {
  DraftJobNavContainer,
  DraftJobNavigationModalContainer,
} from '@/features/jobs/drafting/components/DraftJobView/DraftJobNavContainer'
import { CheckoutViewModel } from '@/features/jobs/drafting/checkout/CheckoutViewModel'
import { QuestionnaireViewModel } from '@/features/jobs/drafting/questionnaire/QuestionnaireViewModel'
import { QuestionnaireStepViewContainer } from '@/features/jobs/drafting/questionnaire/components/QuestionnaireStepViewContainer'
import {
  DraftJobWizardDefaultStickyColumnNavSkeleton,
  DraftJobWizardDefaultWizardContentSkeleton,
} from '@/features/jobs/drafting/components/DraftJobWizardContentSkeleton'
import { DraftJobWizardContentButtonGroupContainer } from '@/features/jobs/drafting/components/DraftJobView/DraftJobWizardContentButtonGroupContainer'
import { useDocumentTitle } from '@/hooks/document-title'
import { UnstyledFormContainer } from '@/components/FormContainer'
import { CheckoutViewContainer } from '@/features/jobs/drafting/checkout/components/CheckoutView/CheckoutViewContainer'
import { RequestedProviderSelectionViewModel } from '@/features/jobs/drafting/requested-provider/RequestedProviderSelectionViewModel'
import { RequestedProviderSelectionViewContainer } from '@/features/jobs/drafting/requested-provider/components/RequestedProviderSelectionView/RequestedProviderSelectionViewContainer'
import { animationFrameScheduler } from 'rxjs'
import { CallAssistanceDialogsContainer } from '@/features/dashboard/components/CallAssistanceDialogs'
import { CallCenterNoticeBannerContainer } from '@/features/dashboard/components/CallCenterNoticeBannerContainer/idex'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { usePageExitMethod } from '@/features/shell/exiting/exit-method-hooks'

/**
 * The DraftJobView
 */
export default observer(function DraftJobView() {
  const { vm } = useLoaderData<{ vm: DraftJobViewModel }>()
  const isSpacious = useIsSpacious()
  const formId = useId()

  useDocumentTitle(vm.job.title)
  useActivation(vm)

  // Job onboarding is a full-screen experience, so hide the global nav bar.
  useHideGlobalNavbar(true)

  // When navigating between items, we don't want to display the indicator.
  // Basically, the route loader will always run, but it'll resolve very quickly
  // due to all the async stuff being cached, but that will cause a quick flip
  // of the navigation state and show the indicator flash.
  useDisableGlobalNavigationIndicator(true)

  const isFirstStep = Boolean(vm.outlineVm.activeStep?.position.first)
  const backButtonBehavior: BackButtonBehavior = isFirstStep ? 'home' : 'back'

  // The current "view" is synced with query parameters for deep-linking.
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Using a layout effect to make sure the URL and the view model's
  // selection state are synced before the browser paints.
  useLayoutEffect(() => {
    const subscription = vm.outlineVm.activeStep$
      .pipe(
        filterNonNil(),
        map((stepVm) => links.editDraftJob(vm.job.id, { step: stepVm.stepId })),
        tap((url) => void navigate(url, { replace: true })),
        whileActive(vm.isPrepared$),
      )
      .subscribe()

    return () => subscription.unsubscribe()
  }, [vm, navigate])

  // Subject where any navigations done via search param changes are pushed through.
  const searchParamsStepChange$ = useSubject<string>()

  // React to changes in the search params by activating the proper step.
  // However, since the VM will automatically select the latest step
  // during preparation, we'll have to defer syncing until the VM
  // has finished preparation.
  useLayoutEffect(() => {
    const subscription = searchParamsStepChange$
      .pipe(
        // Wait until we're prepared before we sync based on
        // the search params.
        switchMap((stepId) =>
          vm.isPrepared$.pipe(
            find(Boolean),
            map(() => stepId),
          ),
        ),
        tap((stepId) =>
          vm.outlineVm.getStepById(stepId)?.becomeActive('Forward'),
        ),
      )
      .subscribe()

    return () => subscription.unsubscribe()
  }, [searchParamsStepChange$, vm])

  // Whenever search params change, send the desired step through the subject.
  const searchParamsOptions = useMemo(
    () => parseSearchParamsToEditDraftJobOptions(searchParams),
    [searchParams],
  )
  useLayoutEffect(() => {
    if (searchParamsOptions) {
      searchParamsStepChange$.next(searchParamsOptions.step)
    }
  }, [searchParamsOptions, searchParamsStepChange$])

  // Make sure step changes result in scrolling to the top.
  useObservable(
    useMemo(
      () =>
        vm.outlineVm.activeStep$.pipe(
          filterNonNil(),
          observeOn(animationFrameScheduler),
          tap(() => document.scrollingElement?.scrollTo({ top: 0 })),
        ),
      [vm],
    ),
  )

  // Redirect to job details on submission.
  useObservable(vm.checkoutVm.submitted$, () => {
    void navigate(
      links.jobDetails(vm.job.id, { showExpectationsDialog: true }),
      { replace: true },
    )
  })

  // Whether the checkout step is the one being shown.
  const isCheckoutStepActive =
    searchParamsOptions?.step === vm.checkoutVm.step.stepId ||
    vm.activeSlice instanceof CheckoutViewModel

  // Determine which view to render.
  let view: ReactNode
  switch (true) {
    // Checkout step uses the entire content area
    case isCheckoutStepActive: {
      view = <CheckoutViewContainer formId={formId} vm={vm} />
      break
    }

    // All other steps share the same layout.
    default: {
      const isSkeleton = vm.prepare.pending
      view = (
        <WizardPageLayout>
          {isSpacious &&
            (isSkeleton ? (
              <DraftJobWizardDefaultStickyColumnNavSkeleton />
            ) : (
              <WizardPageStickyColumn stretch>
                <DraftJobNavContainer vm={vm} />
              </WizardPageStickyColumn>
            ))}
          <WizardPageMainColumn>
            <WizardPageAnimatedContent
              animationKey={
                vm.outlineVm.activeStep?.stepId ??
                searchParamsOptions?.step ??
                'default'
              }
              animationDirection={
                vm.outlineVm.navigationDirection === 'Forward'
                  ? 'forward'
                  : 'back'
              }
            >
              {isSkeleton ? (
                <DraftJobWizardDefaultWizardContentSkeleton />
              ) : (
                <>
                  <UnstyledFormContainer
                    id={formId}
                    onSubmit={(e) => {
                      e.preventDefault()
                      void vm.continue()
                    }}
                  >
                    <CurrentStepViewContainer vm={vm} />
                  </UnstyledFormContainer>

                  <WizardContentFooter>
                    <DraftJobWizardContentButtonGroupContainer
                      form={formId}
                      vm={vm}
                    />
                  </WizardContentFooter>
                </>
              )}
            </WizardPageAnimatedContent>
          </WizardPageMainColumn>
        </WizardPageLayout>
      )
    }
  }

  return (
    <WizardBottomBarPrimaryButtonContextProvider>
      <Wizard testId="draft-job-view">
        <TopBarContainer vm={vm} backButtonBehavior={backButtonBehavior} />
        {view}
        <BottomBarContainer vm={vm} backButtonBehavior={backButtonBehavior} />
      </Wizard>
      <DraftJobNavigationModalContainer vm={vm} />
      <CallAssistanceDialogsContainer vm={vm.callCenterAssistanceVm} />
    </WizardBottomBarPrimaryButtonContextProvider>
  )
})

type BackButtonBehavior = 'home' | 'back'

interface TopBarContainerProps {
  vm: DraftJobViewModel
  backButtonBehavior: BackButtonBehavior
}

/**
 * Top Bar Container
 */
export const TopBarContainer = observer(function TopBarContainer({
  vm,
  backButtonBehavior,
}: TopBarContainerProps) {
  const isSpacious = useIsSpacious()
  const {
    shell: { shellInfo },
  } = useRootStore()
  const lightDark = useLightDarkFn()

  const isCheckout = vm.activeSlice instanceof CheckoutViewModel
  const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()

  return (
    <>
      {!isSpacious && vm.callCenterEnabled && (
        <CallCenterNoticeBannerContainer
          onPress={vm.callCenterAssistanceVm.callOptionsModalVm.show}
        />
      )}

      <WizardTopBar
        // Avoid flashing the last question's text in the header
        key={isCheckout ? 'checkout' : 'default'}
        title={vm.outlineVm.activeStep?.outlineDisplay.title}
        titleTextAnimationKey={vm.outlineVm.activeStep?.stepId}
        {...(isCheckout && {
          titleVisibility: isSpacious ? 'content-exit' : 'always',
          borderVisibility: isSpacious ? 'auto' : 'always',
        })}
        left={
          isSpacious ? (
            <WizardTopBarLogo
              src={lightDark(shellInfo.iconUrl, shellInfo.invertedIconUrl)}
              alt={shellInfo.name}
              testId="top-bar-logo-link"
              {...(isExitMethodVisible && exitMethodProps)}
            />
          ) : backButtonBehavior === 'home' ? (
            isExitMethodVisible && (
              <IconButton
                testId="top-bar-exit-button"
                label="Back to home"
                name="back"
                {...exitMethodProps}
              />
            )
          ) : (
            <IconButton
              label="Previous"
              name="back"
              onPress={vm.outlineVm.goToPrevious}
              testId="top-bar-back-button"
            />
          )
        }
        right={
          <ButtonGroup stretch>
            {isSpacious ? (
              <>
                {vm.callCenterEnabled && (
                  <Button
                    label="Talk to sales"
                    iconLeft="call-start-call"
                    onPress={() =>
                      vm.callCenterAssistanceVm.callOptionsModalVm.show()
                    }
                  />
                )}
                {isExitMethodVisible && (
                  <Button
                    variant="outline"
                    label="Exit"
                    iconLeft="close"
                    testId="top-bar-exit-button"
                    {...exitMethodProps}
                  />
                )}
              </>
            ) : (
              <IconButton
                name="menu"
                label="View menu"
                onPress={vm.outlineModalVm.show}
                testId="top-bar-view-menu-button"
              />
            )}
          </ButtonGroup>
        }
      />
    </>
  )
})

interface BottomBarContainerProps {
  vm: DraftJobViewModel
  backButtonBehavior: BackButtonBehavior
}

/**
 * Bottom Bar Container
 */
const BottomBarContainer = observer(function BottomBarContainer({
  vm,
  backButtonBehavior,
}: BottomBarContainerProps) {
  const isSpacious = useIsSpacious()
  const isSkeleton = vm.prepare.pending

  const primaryButtonProps = useWizardBottomBarPrimaryButton()

  return (
    <WizardBottomBar progress={vm.outlineVm.progress.percent}>
      <WizardBottomBarSection>
        {isSpacious ? (
          backButtonBehavior === 'home' ? (
            <BottomBarExitButtonContainer vm={vm} isSkeleton={isSkeleton} />
          ) : (
            <Button
              variant="text"
              label="Back"
              onPress={vm.outlineVm.goToPrevious}
              testId="bottom-bar-back-button"
            />
          )
        ) : (
          <BottomBarExitButtonContainer vm={vm} isSkeleton={isSkeleton} />
        )}
      </WizardBottomBarSection>
      <WizardBottomBarSpacer />
      <WizardBottomBarSection>
        <ButtonGroup>
          {primaryButtonProps && (
            <WizardBottomBarPrimaryButton
              isSkeleton={isSkeleton}
              {...primaryButtonProps}
            />
          )}
        </ButtonGroup>
      </WizardBottomBarSection>
    </WizardBottomBar>
  )
})

interface BottomBarExitButtonContainerProps {
  vm: DraftJobViewModel
  isSkeleton: boolean
}

/**
 * Bottom Bar Exit Button Container
 */
const BottomBarExitButtonContainer = observer(
  function BottomBarExitButtonContainer({
    vm,
    isSkeleton,
  }: BottomBarExitButtonContainerProps) {
    const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()
    if (isExitMethodVisible) {
      return (
        <Button
          isSkeleton={isSkeleton}
          variant="text"
          label="Exit"
          testId="bottom-bar-exit-button"
          {...exitMethodProps}
        />
      )
    }

    return (
      <WizardBottomBarLabel isSkeleton={isSkeleton}>
        {vm.outlineVm.progress.currentStep} of {vm.outlineVm.progress.total}
      </WizardBottomBarLabel>
    )
  },
)

interface CurrentStepViewContainerProps {
  vm: DraftJobViewModel
}

/**
 * Current Step View
 */
const CurrentStepViewContainer = observer(function CurrentStepView({
  vm,
}: CurrentStepViewContainerProps) {
  switch (true) {
    case vm.activeSlice instanceof QuestionnaireViewModel:
      return <QuestionnaireStepViewContainer vm={vm} />

    case vm.activeSlice instanceof RequestedProviderSelectionViewModel:
      return <RequestedProviderSelectionViewContainer vm={vm} />
  }

  return <></>
})
